import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Clapperboard,
  RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type {
  ProjectListItem,
  ProjectResumeActionKind,
  ProjectStatus,
} from "../../api/types";
import { getThumbnailUrl } from "../../api/useAssetUrl";
import { createNadDebug } from "../../utils/debugLog";
import { projectPreviewCandidates } from "../../utils/projectPreviews";

const recentProjectsLog = createNadDebug("dw:landing-recent-projects");
const recentProjectsTraceId = "landing-recent-projects:flow";

// NOTE: the shelf used to offer a "+ New Project" tile that created an empty
// project and dropped the user on a blank canvas. That entry point was removed
// (Workplace post 1608162530941044) because it bypassed the composer and broke
// onboarding — every project now starts from a prompt in the hero. Its slot in
// the four-column grid is taken by the "All projects" tile.
export interface RecentProjectsShelfState {
  projects: ProjectListItem[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
}

export function formatLandingRelativeTime(
  iso: string,
  locale: string,
  now = Date.now(),
): string {
  const timestamp = new Date(iso).getTime();
  if (Number.isNaN(timestamp)) return iso;

  const formatter = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
    style: "short",
  });
  const elapsedMs = Math.max(0, now - timestamp);
  if (elapsedMs < 60_000) return formatter.format(0, "second");

  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 60) return formatter.format(-minutes, "minute");

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return formatter.format(-hours, "hour");

  const days = Math.floor(hours / 24);
  if (days < 30) return formatter.format(-days, "day");

  const months = Math.floor(days / 30);
  if (months < 12) return formatter.format(-months, "month");

  return formatter.format(-Math.floor(months / 12), "year");
}

export function projectPreviewPath(project: ProjectListItem): string | null {
  return projectPreviewCandidates(project)[0]?.image_path ?? null;
}

function actionKey(status: ProjectStatus): string {
  if (status === "completed") return "recentProjects.actions.review";
  if (status === "in_progress") return "recentProjects.actions.continue";
  return "recentProjects.actions.setup";
}

const resumeActionKeys: Record<ProjectResumeActionKind, string> = {
  answer_question: "recentProjects.actions.answerQuestion",
  review_issue: "recentProjects.actions.reviewIssue",
  check_progress: "recentProjects.actions.checkProgress",
  start_story: "recentProjects.actions.startStory",
  continue_design: "recentProjects.actions.continueDesign",
  continue_script: "recentProjects.actions.continueScript",
  continue_storyboard: "recentProjects.actions.continueStoryboard",
  continue_video: "recentProjects.actions.continueVideo",
  continue_audio: "recentProjects.actions.continueAudio",
  continue_project: "recentProjects.actions.continue",
  review_project: "recentProjects.actions.review",
};

export function projectWorkspacePath(project: ProjectListItem): string {
  const basePath = `/project/${project.id}/workspace`;
  const canvasId = project.resume_action?.canvas_id;
  if (!canvasId || canvasId === "agent") return basePath;
  return `${basePath}/${encodeURIComponent(canvasId)}`;
}

function ShelfFrame({ children }: { children: ReactNode }) {
  return (
    <div className="w-[82vw] max-w-[320px] shrink-0 snap-start sm:w-auto sm:max-w-none lg:min-w-0">
      {children}
    </div>
  );
}

function ProjectThumbnail({ project }: { project: ProjectListItem }) {
  const { t } = useTranslation("landing");
  const [failed, setFailed] = useState(false);
  const previewPath = projectPreviewPath(project);
  const thumbnailUrl = previewPath
    ? getThumbnailUrl(project.id, previewPath, 480)
    : null;

  if (!thumbnailUrl || failed) {
    return (
      <div
        data-testid={`recent-project-thumbnail-fallback-${project.id}`}
        className="flex aspect-[3/1] w-full items-center justify-center bg-surface-1 text-muted-foreground"
      >
        <Clapperboard
          className="h-6 w-6"
          strokeWidth={1.5}
          aria-hidden="true"
        />
      </div>
    );
  }

  // Letterboxed, never cropped. A fixed-height `object-cover` box clipped the
  // subject out of portrait and ultra-wide previews, which read as a broken
  // visual state (Workplace post 1608162530941044, "yellow zone"). A 16:9 box
  // plus `object-contain` shows the whole frame and pads the remainder, so the
  // card grid still lines up.
  return (
    <div className="aspect-[3/1] w-full overflow-hidden bg-black">
      <img
        src={thumbnailUrl}
        alt={t("recentProjects.thumbnailAlt", { name: project.name })}
        onError={() => setFailed(true)}
        className="h-full w-full object-contain"
      />
    </div>
  );
}

function ProjectCard({ project }: { project: ProjectListItem }) {
  const { t, i18n } = useTranslation("landing");
  const edited = useMemo(
    () => formatLandingRelativeTime(project.updated_at, i18n.language),
    [i18n.language, project.updated_at],
  );
  const resumeAction = project.resume_action;
  const questionTopic =
    resumeAction?.kind === "answer_question" && resumeAction.label
      ? resumeAction.label.toLocaleLowerCase(i18n.language)
      : null;
  const actionLabel =
    questionTopic
      ? t("recentProjects.actions.answerQuestionAbout", {
          label: questionTopic,
        })
      : t(
          resumeAction
            ? resumeActionKeys[resumeAction.kind]
            : actionKey(project.status),
        );

  return (
    <Link
      to={projectWorkspacePath(project)}
      data-testid={`recent-project-card-${project.id}`}
      aria-label={t("recentProjects.openProject", { name: project.name })}
      onClick={() => {
        recentProjectsLog({
          event: "recent-projects:project-selected",
          trace_id: recentProjectsTraceId,
          project_id: project.id,
          project_status: project.status,
          resume_action_kind: resumeAction?.kind ?? null,
          resume_action_canvas_id: resumeAction?.canvas_id ?? null,
          has_resume_action_label: Boolean(resumeAction?.label),
        });
      }}
      className="group flex h-48 flex-col overflow-hidden rounded-[20px] border-[1.5px] border-white/50 bg-[rgba(42,42,42,0.7)] text-left text-white no-underline shadow-[0_4px_4px_rgba(0,0,0,0.25)] backdrop-blur-[20px] transition-[background-color,border-color,transform] duration-200 ease-out hover:-translate-y-0.5 hover:border-white/70 hover:bg-[rgba(42,42,42,0.82)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 sm:h-52 lg:h-36 xl:h-32"
    >
      <ProjectThumbnail project={project} />
      <div className="flex min-h-0 flex-1 flex-col p-2">
        <div className="flex min-w-0 flex-col gap-0.5 xl:flex-row xl:items-center xl:gap-2">
          <span className="min-w-0 flex-1 truncate font-[Fustat] text-sm font-semibold text-foreground">
            {project.name}
          </span>
          <span className="shrink-0 font-sans text-xs font-medium text-muted-foreground">
            {edited}
          </span>
        </div>
        <div className="mt-auto flex items-center gap-1.5 rounded-lg border border-white/20 bg-background/60 px-2 py-1 text-xs font-medium text-foreground transition-[background-color,border-color] group-hover:border-white/40 group-hover:bg-white/10">
          <span className="min-w-0 flex-1 truncate">
            {actionLabel}
          </span>
          <ArrowUpRight
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </div>
      </div>
    </Link>
  );
}

function LoadingCard({ index }: { index: number }) {
  return (
    <div
      data-testid={`recent-project-skeleton-${index}`}
      className="skeleton h-48 rounded-[20px] border-[1.5px] border-white/50 bg-[rgba(42,42,42,0.7)] shadow-[0_4px_4px_rgba(0,0,0,0.25)] backdrop-blur-[20px] sm:h-52 lg:h-36 xl:h-32"
      aria-hidden="true"
    >
      <div className="h-28 bg-surface-2 lg:h-24" />
      <div className="space-y-3 p-2.5">
        <div className="h-3 w-3/4 rounded bg-surface-2" />
        <div className="h-7 rounded-lg bg-surface-1" />
      </div>
    </div>
  );
}

export default function RecentProjectsShelf({
  projects,
  isLoading,
  error,
  onRetry,
}: RecentProjectsShelfState) {
  const { t } = useTranslation("landing");
  const renderSignatureRef = useRef("");
  const hasError = Boolean(error);
  const state = isLoading
    ? "loading"
    : hasError
      ? "error"
      : projects.length > 0
        ? "populated"
        : "empty";

  useEffect(() => {
    const signature = `${state}:${projects.length}`;
    if (renderSignatureRef.current === signature) return;
    renderSignatureRef.current = signature;
    recentProjectsLog({
      event: hasError
        ? "recent-projects:load-failed"
        : "recent-projects:rendered",
      trace_id: recentProjectsTraceId,
      state,
      project_count: projects.length,
      error_type: error?.name ?? null,
    });
  }, [error, hasError, projects.length, state]);

  return (
    <section
      data-testid="recent-projects-shelf"
      aria-label={t("recentProjects.label")}
      className="mt-3"
    >
      <h2 className="sr-only">{t("recentProjects.label")}</h2>
      <div
        data-testid="recent-projects-track"
        className="scrollbar-hide flex touch-pan-x snap-x snap-mandatory gap-2 overflow-x-auto overscroll-x-contain pb-2 sm:grid sm:grid-cols-2 sm:overflow-visible sm:snap-none lg:grid-cols-4"
      >
        {isLoading &&
          [0, 1, 2].map((index) => (
            <ShelfFrame key={index}>
              <LoadingCard index={index} />
            </ShelfFrame>
          ))}

        {!isLoading && hasError && (
          <div className="flex h-48 w-[82vw] max-w-[320px] shrink-0 snap-start items-center justify-center rounded-[20px] border-[1.5px] border-destructive/50 bg-[rgba(42,42,42,0.7)] px-5 text-center shadow-[0_4px_4px_rgba(0,0,0,0.25)] backdrop-blur-[20px] sm:col-span-1 sm:h-52 sm:w-auto sm:max-w-none lg:col-span-3 lg:h-36 xl:h-32">
            <div>
              <p className="text-sm text-foreground">
                {t("recentProjects.loadError")}
              </p>
              <button
                type="button"
                onClick={onRetry}
                className="mx-auto mt-3 flex h-7 items-center gap-2 rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground transition-[background-color,border-color] hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                {t("recentProjects.retry")}
              </button>
            </div>
          </div>
        )}

        {!isLoading && !hasError && projects.length === 0 && (
          <div className="flex h-48 w-[82vw] max-w-[320px] shrink-0 snap-start items-center justify-center rounded-[20px] border-[1.5px] border-white/50 bg-[rgba(42,42,42,0.7)] px-5 text-center shadow-[0_4px_4px_rgba(0,0,0,0.25)] backdrop-blur-[20px] sm:col-span-1 sm:h-52 sm:w-auto sm:max-w-none lg:col-span-3 lg:h-36 xl:h-32">
            <div>
              <Clapperboard
                className="mx-auto h-6 w-6 text-muted-foreground"
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <p className="mt-3 text-sm font-medium text-foreground">
                {t("recentProjects.emptyTitle")}
              </p>
              <p className="mt-1 text-xs font-medium text-muted-foreground">
                {t("recentProjects.emptyDescription")}
              </p>
            </div>
          </div>
        )}

        {!isLoading &&
          !hasError &&
          projects.map((project) => (
            <ShelfFrame key={project.id}>
              <ProjectCard project={project} />
            </ShelfFrame>
          ))}

        {/* "All projects" is a TILE in the grid, not a link underneath it, so
            the shelf reads as one row of four equal cards (Rowell's mockup).
            It occupies the slot the removed "+ New Project" tile used to hold. */}
        <ShelfFrame>
          <Link
            to="/dashboard"
            data-testid="recent-projects-all-link"
            onClick={() =>
              recentProjectsLog({
                event: "recent-projects:all-projects-selected",
                trace_id: recentProjectsTraceId,
              })
            }
            className="group flex h-48 w-full flex-col items-center justify-center gap-3 rounded-[20px] border-[1.5px] border-white/50 bg-[rgba(42,42,42,0.7)] text-white no-underline shadow-[0_4px_4px_rgba(0,0,0,0.25)] backdrop-blur-[20px] transition-[background-color,border-color,transform] duration-200 ease-out hover:-translate-y-0.5 hover:border-white/70 hover:bg-[rgba(42,42,42,0.82)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 sm:h-52 lg:h-36 xl:h-32"
          >
            <ArrowRight
              className="h-6 w-6 text-muted-foreground transition-colors group-hover:text-foreground"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <span className="font-[Fustat] text-base font-semibold">
              {t("recentProjects.allProjects")}
            </span>
          </Link>
        </ShelfFrame>
      </div>
    </section>
  );
}
